import { createContext, useContext, useEffect, useState, useRef } from "react";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  onAuthStateChanged,
  signOut,
  GoogleAuthProvider,
  signInWithPopup,
  RecaptchaVerifier,
} from "firebase/auth";
import {
  getFirestore,
  doc,
  setDoc,
  getDoc,
  updateDoc,
  collection,
  query,
  where,
  getDocs,
} from "firebase/firestore";
import { auth } from "../firebase";

const userAuthContext = createContext();

const AUTH_VERIFICATION_TIMEOUT = 10000;

export function UserAuthContextProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userDataLoading, setUserDataLoading] = useState(false);
  const fetchAttemptedRef = useRef(false);
  const firestore = useRef(getFirestore()); // Instancia única de Firestore

  function logIn(email, password) {
    return signInWithEmailAndPassword(auth, email, password);
  }

  // ✅ FUNCIÓN COMPLETA: Crear/Actualizar documento de usuario
  async function createOrUpdateUserDocument(userObj, email) {
    const userRef = doc(firestore.current, "Users", userObj.uid);

    try {
      // Verificar si existe
      const docSnap = await getDoc(userRef);

      if (!docSnap.exists()) {
        // ✅ CREAR NUEVO USUARIO con tipo "cliente" por defecto
        await setDoc(userRef, {
          uid: userObj.uid,
          email: email,
          type: "cliente", // ← POR DEFECTO: cliente
          role: "cliente", // Sistema de roles mejorado
          displayName: userObj.displayName || "",
          photoURL: userObj.photoURL || "",
          timestamp: new Date(),
          lastLogin: new Date(),
          emailVerified: userObj.emailVerified || false,
          phoneNumber: userObj.phoneNumber || "",
          createdAt: new Date(),
          isActive: true,
          permissions: {
            canAccessAdmin: false,
            canManageUsers: false,
            canManageProducts: false,
            canManageStores: false,
            canViewReports: false,
            canProcessOrders: false,
          },
        });
        console.log("✅ Usuario CREADO con tipo 'cliente':", userObj.uid);
      } else {
        // ✅ ACTUALIZAR usuario existente
        await updateDoc(userRef, {
          lastLogin: new Date(),
          emailVerified: userObj.emailVerified || false,
          isActive: true,
        });
        console.log("✅ Usuario ACTUALIZADO:", userObj.uid);
      }

      return true;
    } catch (error) {
      console.error("❌ Error con documento de usuario:", error);
      throw error;
    }
  }

  function signUp(email, password) {
    return createUserWithEmailAndPassword(auth, email, password).then(
      async (result) => {
        // Crear documento inmediatamente después del registro
        await createOrUpdateUserDocument(result.user, email);
        return result;
      }
    );
  }

  function logOut() {
    fetchAttemptedRef.current = false;
    setUser(null);
    return signOut(auth);
  }

  function googleSignIn() {
    const googleAuthProvider = new GoogleAuthProvider();
    return signInWithPopup(auth, googleAuthProvider).then(async (result) => {
      await createOrUpdateUserDocument(result.user, result.user.email);
      return result;
    });
  }

  function setUpRecaptha(number) {
    const recaptchaVerifier = new RecaptchaVerifier(
      "recaptcha-container",
      {},
      auth
    );
    recaptchaVerifier.render();
  }

  // ✅ FUNCIÓN COMPLETA: Obtener datos del usuario DIRECTAMENTE de Firestore
  async function fetchUserData(uid) {
    if (fetchAttemptedRef.current) {
      console.log("⏭️ fetchUserData ya ejecutado");
      setUserDataLoading(false);
      return;
    }

    fetchAttemptedRef.current = true;
    setUserDataLoading(true);
    console.log("🔄 Cargando datos de usuario desde Firestore:", uid);

    try {
      const userRef = doc(firestore.current, "Users", uid);
      const docSnap = await getDoc(userRef);

      if (docSnap.exists()) {
        const userData = docSnap.data();
        console.log("📄 Datos encontrados:", {
          uid,
          type: userData.type,
          email: userData.email,
        });

        // Actualizar usuario con datos de Firestore
        setUser((prevUser) => {
          if (prevUser && prevUser.uid === uid) {
            const updatedUser = {
              ...prevUser, // Mantener datos de Firebase Auth
              // ← DATOS CRÍTICOS DE FIRESTORE
              type: userData.type || "cliente",
              role: userData.role || userData.type || "cliente",
              permissions: userData.permissions || {
                canAccessAdmin: false,
                canManageUsers: false,
                canManageProducts: false,
                canManageStores: false,
                canViewReports: false,
                canProcessOrders: false,
              },
              nombre:
                userData.nombre || userData.displayName || prevUser.displayName,
              photoUrl: userData.photoURL || prevUser.photoURL,
              // Datos adicionales de Firestore
              phoneNumber: userData.phoneNumber || prevUser.phoneNumber,
              createdAt: userData.createdAt || null,
              isActive: userData.isActive !== false, // true por defecto
              // ← MANTENER DATOS DE AUTH
              uid: prevUser.uid,
              email: prevUser.email,
              emailVerified: prevUser.emailVerified,
              displayName: prevUser.displayName,
              photoURL: prevUser.photoURL,
            };

            console.log("✅ Usuario COMPLETO:", {
              uid: updatedUser.uid,
              type: updatedUser.type,
              email: updatedUser.email,
              isActive: updatedUser.isActive,
            });

            return updatedUser;
          }
          return prevUser;
        });
      } else {
        console.warn("⚠️ Documento NO existe para UID:", uid);
        // Crear documento por defecto
        await createOrUpdateUserDocument(
          auth.currentUser,
          auth.currentUser?.email
        );
        // No reintentar para evitar loops - el próximo login lo cargará
      }
    } catch (error) {
      console.error("❌ Error cargando datos de Firestore:", error);
    } finally {
      setUserDataLoading(false);
    }
  }

  // ✅ NUEVA FUNCIÓN: Actualizar rol de usuario con permisos
  async function updateUserRole(uid, newRole) {
    const validRoles = ["admin", "gerente", "cajero", "cliente"];
    if (!uid || !validRoles.includes(newRole)) {
      throw new Error("Rol inválido");
    }

    // Definir permisos según rol
    const rolePermissions = {
      admin: {
        canAccessAdmin: true,
        canManageUsers: true,
        canManageProducts: true,
        canManageStores: true,
        canViewReports: true,
        canProcessOrders: true,
      },
      gerente: {
        canAccessAdmin: true,
        canManageUsers: false,
        canManageProducts: true,
        canManageStores: true,
        canViewReports: true,
        canProcessOrders: true,
      },
      cajero: {
        canAccessAdmin: true,
        canManageUsers: false,
        canManageProducts: false,
        canManageStores: false,
        canViewReports: false,
        canProcessOrders: true,
      },
      cliente: {
        canAccessAdmin: false,
        canManageUsers: false,
        canManageProducts: false,
        canManageStores: false,
        canViewReports: false,
        canProcessOrders: false,
      },
    };

    try {
      const userRef = doc(firestore.current, "Users", uid);
      await updateDoc(userRef, {
        type: newRole,
        role: newRole,
        permissions: rolePermissions[newRole],
        updatedAt: new Date(),
      });

      // Actualizar estado local
      setUser((prevUser) =>
        prevUser
          ? {
              ...prevUser,
              type: newRole,
              role: newRole,
              permissions: rolePermissions[newRole],
            }
          : prevUser
      );
      console.log("✅ Rol actualizado a:", newRole);
      return true;
    } catch (error) {
      console.error("❌ Error actualizando rol:", error);
      throw error;
    }
  }

  // Mantener compatibilidad con código existente
  async function updateUserType(uid, newType) {
    return updateUserRole(uid, newType);
  }

  // ✅ NUEVA FUNCIÓN: Verificar si es admin
  async function isAdmin(uid) {
    try {
      const userRef = doc(firestore.current, "Users", uid);
      const docSnap = await getDoc(userRef);
      return docSnap.exists() && docSnap.data().type === "admin";
    } catch (error) {
      console.error("Error verificando admin:", error);
      return false;
    }
  }

  // ✅ NUEVA FUNCIÓN: Obtener todos los admins
  async function getAllAdmins() {
    try {
      const usersRef = collection(firestore.current, "Users");
      const q = query(usersRef, where("type", "==", "admin"));
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
    } catch (error) {
      console.error("Error obteniendo admins:", error);
      return [];
    }
  }

  // ✅ NUEVA FUNCIÓN: Obtener usuario por email
  async function getUserByEmail(email) {
    try {
      const usersRef = collection(firestore.current, "Users");
      const q = query(usersRef, where("email", "==", email));
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.length > 0
        ? {
            id: querySnapshot.docs[0].id,
            ...querySnapshot.docs[0].data(),
          }
        : null;
    } catch (error) {
      console.error("Error buscando usuario por email:", error);
      return null;
    }
  }

  // EFECTO PRINCIPAL - Todo directo con Firestore
  useEffect(() => {
    console.log("🚀 Iniciando UserAuthContext con Firestore directo");

    let timeoutId;
    let authUnsubscribe;

    timeoutId = setTimeout(() => {
      if (loading) {
        console.warn("⏰ Timeout de autenticación");
        setLoading(false);
      }
    }, AUTH_VERIFICATION_TIMEOUT);

    authUnsubscribe = onAuthStateChanged(auth, (currentUser) => {
      console.log("🔐 Auth changed:", currentUser?.email || "No user");

      if (currentUser) {
        // Usuario básico primero
        const basicUser = { ...currentUser, type: null };
        setUser(basicUser);

        // Cargar datos completos de Firestore
        fetchUserData(currentUser.uid);
      } else {
        setUser(null);
        fetchAttemptedRef.current = false;
      }

      setLoading(false);
    });

    return () => {
      clearTimeout(timeoutId);
      if (authUnsubscribe) authUnsubscribe();
    };
  }, []);

  // ✅ Obtener todos los usuarios (solo admins)
  async function getAllUsers() {
    try {
      const usersRef = collection(firestore.current, "Users");
      const querySnapshot = await getDocs(usersRef);
      return querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
    } catch (error) {
      console.error("Error obteniendo usuarios:", error);
      return [];
    }
  }

  // ✅ Verificar si tiene permiso específico
  function hasPermission(permission) {
    if (!user || !user.permissions) return false;
    return user.permissions[permission] === true;
  }

  const value = {
    user,
    loading,
    userDataLoading,
    logIn,
    signUp,
    logOut,
    googleSignIn,
    setUpRecaptha,
    // ✅ NUEVAS FUNCIONES FIRESTORE DIRECTO
    updateUserType,
    updateUserRole,
    isAdmin,
    getAllAdmins,
    getAllUsers,
    getUserByEmail,
    hasPermission,
    // Refrescar datos
    refreshUser: (uid) => {
      fetchAttemptedRef.current = false;
      fetchUserData(uid);
    },
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-orange-50 to-white">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">🐔 Pollo Feliz</p>
          <p className="text-sm text-gray-500 mt-1">
            Conectando con Firebase...
          </p>
        </div>
      </div>
    );
  }

  return (
    <userAuthContext.Provider value={value}>
      {children}
      {userDataLoading && (
        <div className="fixed bottom-4 right-4 bg-white/90 backdrop-blur-sm p-2 rounded-full shadow-lg border border-orange-200 z-50">
          <div className="w-5 h-5 border-2 border-orange-400 border-t-transparent rounded-full animate-spin"></div>
        </div>
      )}
    </userAuthContext.Provider>
  );
}

export function useUserAuth() {
  const context = useContext(userAuthContext);
  if (context === undefined) {
    throw new Error(
      "useUserAuth debe usarse dentro de UserAuthContextProvider"
    );
  }
  return context;
}
